import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Calendar, Clock, Users } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";
import { Spinner, EmptyState } from "../../components/common/UI";

const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-yellow-100 text-yellow-800",
    confirmed: "bg-green-100 text-green-800",
    cancelled: "bg-red-100 text-red-800",
  };
  return (
    <span className={`badge ${map[status] || ""} capitalize`}>{status}</span>
  );
};

export default function MyBookingsPage() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("bookings")
      .select("*, restaurants(name, slug, logo_url)")
      .eq("user_id", user.id)
      .order("date", { ascending: false })
      .order("time", { ascending: false })
      .then(({ data }) => {
        setBookings(data || []);
        setLoading(false);
      });
  }, [user]);

  return (
    <>
      <Helmet><title>My Bookings — Khana Spot</title></Helmet>
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 page-enter">
        <h1 className="font-display text-2xl font-bold text-gray-900 mb-6">My Bookings</h1>

        {loading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : bookings.length === 0 ? (
          <EmptyState
            icon="🪑"
            title="No bookings yet"
            description="Reserve a table at your favourite restaurant!"
            action={<Link to="/restaurants" className="btn-primary">Browse Restaurants</Link>}
          />
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <div key={booking.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  {booking.restaurants?.logo_url ? (
                    <img
                      src={booking.restaurants.logo_url}
                      alt=""
                      className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0 text-xl">
                      🍽️
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <Link
                        to={`/restaurant/${booking.restaurants?.slug}`}
                        className="font-semibold text-gray-900 hover:text-orange-600 transition-colors"
                      >
                        {booking.restaurants?.name}
                      </Link>
                      <StatusBadge status={booking.status} />
                    </div>

                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4 text-orange-400" />
                        {new Date(booking.date).toLocaleDateString("en-PK", {
                          weekday: "short", day: "numeric", month: "short", year: "numeric"
                        })}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-orange-400" />
                        {booking.time}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Users className="w-4 h-4 text-orange-400" />
                        {booking.guests} {booking.guests === 1 ? "Guest" : "Guests"}
                      </span>
                    </div>

                    {booking.notes && (
                      <p className="text-xs text-gray-400 mt-2">📝 {booking.notes}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
